import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { VerifyPayments } from "../../../api/student";
import { currencyFormatter } from "../../../utils";
import { getHostelList, hostelError, hostelPaymentStatus, isHostelPaymentConfirmed } from "../../../utils/hostel";

export default function AllHostelPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(payments.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const loadPayments = async () => {
    setLoading(true);
    setError("");
    try { setPayments(await getHostelList("/hostel/applications/payments/")); }
    catch (err) { setError(hostelError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPayments(); }, []);
  const requery = async (payment) => {
    setVerifying(payment.id);
    try {
      await VerifyPayments(payment.transaction_ref);
      await loadPayments();
      toast.info("Payment status refreshed. Check the updated status below.");
    } catch (err) { toast.error(hostelError(err)); }
    finally { setVerifying(null); }
  };
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-brand-darkGray">Accommodation Payment History</h1>
      <button className="text-[#5D71C0]" onClick={() => navigate("/accommodation")}>Back to accommodation</button>
      <div className="p-6 bg-white rounded-xl space-y-4">
        <button onClick={loadPayments} disabled={loading || verifying !== null} className="btn btn-primary">Refresh</button>
        {loading && <p role="status">Loading payments...</p>}
        {error && <p role="alert" className="text-red-600">{error} Use Refresh to retry.</p>}
        {!loading && !error && payments.length === 0 && <p>No accommodation payments yet.</p>}
        {!loading && !error && payments.length > 0 && <>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr>{["Hostel / Room / Bed", "Session", "Reference", "Amount", "Date", "Status", "Action"].map(label => <th key={label} className="p-3">{label}</th>)}</tr></thead>
              <tbody>{payments.slice((currentPage - 1) * pageSize, currentPage * pageSize).map(payment => (
                <tr key={payment.id} className="border-b">
                  <td className="p-3">{payment.hostel?.name || "—"} / {payment.room?.room_number || "—"} / {payment.allocated_bed || "—"}</td>
                  <td className="p-3">{payment.session_name || "—"}</td>
                  <td className="p-3">{payment.transaction_ref || "—"}</td>
                  <td className="p-3">{currencyFormatter(payment.amount_paid)}</td>
                  <td className="p-3">{payment.confirmed_at || payment.paid_at || payment.date_paid ? moment(payment.confirmed_at || payment.paid_at || payment.date_paid).format("DD MMM YYYY, h:mm A") : "Awaiting confirmation"}</td>
                  <td className="p-3">{hostelPaymentStatus(payment)}</td>
                  <td className="p-3">{isHostelPaymentConfirmed(payment) ? (
                    <button className="text-green-700" onClick={() => navigate(`/invoice/accommodation?reference=${encodeURIComponent(payment.transaction_ref)}`, { state: { invoice: payment } })}>Print receipt</button>
                  ) : (
                    <button className="text-[#5D71C0]" disabled={verifying !== null || !payment.transaction_ref} onClick={() => requery(payment)}>{verifying === payment.id ? "Verifying..." : "Requery"}</button>
                  )}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="flex items-center gap-3">
            <button disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>Next</button>
          </div>
        </>}
      </div>
    </div>
  );
}
